import { Injectable, inject } from '@angular/core';
import { LocationService } from './location.service';
import { WidgetService } from './widget.service';
import { VehicleService } from '../vehicle/vehicle.service';
import { Position } from '../../models/position';

/**
 * JavaScript API of the widget (widget host) for the embedding site.
 *
 * Registered on `window` (and answered via postMessage for iframe embeddings) once
 * MainComponent starts in widget mode:
 *
 *   window.evWidget.gpsPositionUpdate(lat, lng, speed?, heading?, accuracy?, altitude?, timestamp?)
 *   window.evWidget.gpsPositionUpdate({ lat, lng | lon, speed?, heading?, accuracy?, altitude?, timestamp? })
 *   window.gpsPositionUpdate(...)                      alias of the above
 *   window.evWidget.setBattery(level)                  state of charge, 1..100
 *
 *   iframe.contentWindow.postMessage({ type: 'gpsPositionUpdate', lat, lng, speed?, heading?, ... }, '*')
 *   iframe.contentWindow.postMessage({ type: 'setBattery', level }, '*')
 *
 * Speed is m/s, heading degrees, accuracy/altitude metres, timestamp ms since epoch.
 */
export interface WidgetGpsPosition {
  lat: number;
  lng?: number;
  lon?: number;
  speed?: number;
  heading?: number;
  accuracy?: number;
  altitude?: number;
  timestamp?: number;
}

@Injectable({ providedIn: 'root' })
export class WidgetApiService {

  private readonly widgetService = inject(WidgetService);
  private readonly locationService = inject(LocationService);
  private readonly vehicleService = inject(VehicleService);
  private initialized = false;

  public init(): void {
    if (this.initialized || !this.widgetService.isWidget()) return;
    this.initialized = true;

    const api = {
      gpsPositionUpdate: (...args: any[]) => this.gpsPositionUpdate(...args),
      setBattery: (level: number) => this.setBattery(level),
    };
    (window as any)['evWidget'] = api;
    (window as any)['gpsPositionUpdate'] = api.gpsPositionUpdate;

    window.addEventListener('message', (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'gpsPositionUpdate') {
        this.gpsPositionUpdate(data);
      } else if (data.type === 'setBattery') {
        this.setBattery(data.level);
      }
    });
  }

  /** Accepts either positional (lat, lng, speed, heading, accuracy, altitude, timestamp) or one object. */
  public gpsPositionUpdate(...args: any[]): boolean {
    let p: WidgetGpsPosition;
    if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
      p = args[0];
    } else {
      const [lat, lng, speed, heading, accuracy, altitude, timestamp] = args;
      p = { lat, lng, speed, heading, accuracy, altitude, timestamp };
    }
    const lat = Number(p.lat);
    const lng = Number(p.lng ?? p.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      return false;
    }
    const num = (v: any, fallback: number) => (Number.isFinite(Number(v)) && v != null ? Number(v) : fallback);
    this.locationService.setLastPosition(new Position(
      lat, lng,
      num(p.speed, 0), num(p.accuracy, 0), num(p.altitude, 0), 0,
      num(p.heading, 0), num(p.timestamp, Date.now()),
    ));
    return true;
  }

  public setBattery(level: number): boolean {
    const value = Number(level);
    if (!Number.isFinite(value)) return false;
    const clamped = Math.min(100, Math.max(1, value));
    this.vehicleService.setBattery(clamped);
    // Moves the vehicle panel slider (and persists the level like a user change would).
    this.vehicleService.ObservableBatteryChanged.next(clamped);
    return true;
  }
}
